import DataGrid, { Column, Format, Summary, TotalItem } from "devextreme-react/data-grid";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import { formatNumber } from "devextreme/localization";
import { useContext } from "react";
import { confirm } from 'devextreme/ui/dialog';
import { LoadPopupPanel } from "../../../../components";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { useResubmitChangeOrder } from "../api/resubmit-change-order";
import { RefetchScope, RetailOrderContext } from "../context/RetailOrderContext";
import { ChangeOrderProduct } from "../types/RetailOrderDetails";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AR } from "../../../../types/enums";
import { useSendSigningLink } from "../api/send-signing-link";
import { alert } from "devextreme/ui/dialog";

export default function ChangeRequestPopup(props: { onHidePopup: () => void }) {

	const { order, refetchOrder } = useContext(RetailOrderContext);
	const resubmitChangeOrder = useResubmitChangeOrder(order.details.retailOrderID,
		() => {
			showSuccessMessage('Change Order has been resubmitted');
			refetchOrder(RefetchScope.Notes);
		},
		(error) => showErrorMessage(error)
	);

	const sendSigningLink = useSendSigningLink(order.details.retailOrderID,
		() => {
			showSuccessMessage('Signing link has been sent');
			refetchOrder(RefetchScope.Notes);
		},
		(error) => alert(error, "Error")
	);

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const returnUrl = encodeURIComponent(searchParams.get("returnUrl") ?? "");

	return (
		<>
			<Popup
				visible={true}
				title="Change Request"
				hideOnOutsideClick={false}
				onHiding={() => props.onHidePopup()}
			>
				<ToolbarItem
					disabled={resubmitChangeOrder.isLoading || sendSigningLink.isLoading}
					options={{
						icon: 'dx-icon dx-icon-close',
						onClick: () => props.onHidePopup()
					}}
					widget="dxButton"
					location="after"
				/>
				<p>
					Original order: <b>{order.details.roNumber}</b>
				</p>
				<DataGrid
					dataSource={order.details.products}
					allowColumnReordering={true}
					allowColumnResizing={true}
					showBorders={true}
					showColumnLines={true}
					columnAutoWidth={false}
					keyExpr={'roProductID'}
				>
					<Column
						dataField={'itemDescription'}
						caption={'Item Description'}
						dataType="string"
						width={400}
					/>
					<Column
						dataField={'productLineDescription'}
						caption={'Product Line'}
						dataType="string"
					/>
					<Column
						dataField={'dealerSKU'}
						caption={'SKU'}
						dataType="string"
					/>
					<Column
						dataField={'dealerUOM'}
						caption={'UOM'}
						dataType="string"
					/>
					<Column
						dataField={'dealerQty'}
						caption={'Qty'}
						dataType="number"
						width={70}
					/>
					<Column
						dataField={'cfiPrice'}
						caption={'CFI Price'}
						dataType="number"
					>
						<Format type="currency" precision={2} ></Format>
					</Column>
					<Column
						dataField={'cfiExtendedPrice'}
						caption={'CFI Extended Price'}
						dataType="number"
					>
						<Format type="currency" precision={2} ></Format>
					</Column>
					<Summary>
						<TotalItem
							column="cfiExtendedPrice"
							summaryType="sum"
							customizeText={(data: any) => 'Total: ' + formatNumber(data.value, { type: 'currency', precision: 2 })}
						/>
					</Summary>
				</DataGrid>
				<p>
					New order
				</p>
				<DataGrid
					dataSource={order.details.changeOrderProducts}
					allowColumnReordering={true}
					allowColumnResizing={true}
					showBorders={true}
					showColumnLines={true}
					columnAutoWidth={false}
					keyExpr={'roProductChangeRequestID'}
				>
					<Column
						dataField={'itemDescription'}
						caption={'Item Description'}
						dataType="string"
						width={400}
					/>
					<Column
						dataField={'productLineDescription'}
						caption={'Product Line'}
						dataType="string"
					/>
					<Column
						dataField={'dealerSKU'}
						caption={'SKU'}
						dataType="string"
					/>
					<Column
						dataField={'dealerUOM'}
						caption={'UOM'}
						dataType="string"
					/>
					<Column
						dataField={'dealerQty'}
						caption={'Qty'}
						dataType="number"
						width={70}
					/>
					<Column
						dataField={'cfiPrice'}
						caption={'CFI Price'}
						dataType="number"
					>
						<Format type="currency" precision={2} ></Format>
					</Column>
					<Column
						dataField={'cfiExtendedPrice'}
						caption={'CFI Extended Price'}
						dataType="number"
						calculateCellValue={(row: ChangeOrderProduct) => { return row.cfiPrice * row.dealerQty }}
					>
						<Format type="currency" precision={2} ></Format>
					</Column>
					<Summary>
						<TotalItem
							column="cfiExtendedPrice"
							summaryType="sum"
							customizeText={(data: any) => 'Total: ' + formatNumber(data.value, { type: 'currency', precision: 2 })}
						/>
					</Summary>
				</DataGrid>
				<ToolbarItem
					visible={order.details.IME && !!order.details.projectNumber &&
						!order.details.activeARs.includes(AR.PendingPostTemplatePayment)}
					disabled={resubmitChangeOrder.isLoading || sendSigningLink.isLoading}
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Send Signing Link',
						elementAttr: { 'data-testid': 'btn-send-signing-link' },
						onClick: () => {
							let result = confirm("Are you sure you want to send signing link?", "Signing Link");
							result.then(function (dialogResult) {
								if (dialogResult) {
									sendSigningLink.mutate();
								}
							});
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				/>
				<ToolbarItem
					visible={order.details.activeARs.includes(AR.DealerToConfirmChangeOrder)}
					disabled={resubmitChangeOrder.isLoading || sendSigningLink.isLoading}
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Edit Change Request',
						elementAttr: { 'data-testid': 'btn-edit-change-request' },
						onClick: () => {
							navigate(`/cfi/retail-order/${order.details.retailOrderID}/120/change-order` +
								(!!returnUrl ? '?returnUrl=' + returnUrl : ''));
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				/>
				<ToolbarItem
					visible={order.details.activeARs.includes(AR.DealerToConfirmChangeOrder)}
					disabled={resubmitChangeOrder.isLoading || sendSigningLink.isLoading}
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Resubmit Change Request',
						elementAttr: { 'data-testid': 'btn-resubmit-change-request' },
						onClick: () => {
							let result = confirm("Are you sure you want to resubmit the change request?", "Resubmit");
							result.then(function (dialogResult) {
								if (dialogResult) {
									resubmitChangeOrder.mutate();
								}
							});
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				/>
				<ToolbarItem
					visible={order.details.activeARs.includes(AR.DealerToConfirmChangeOrder)}
					disabled={resubmitChangeOrder.isLoading || sendSigningLink.isLoading}
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Cancel',
						elementAttr: { 'data-testid': 'btn-cancel' },
						onClick: () => props.onHidePopup()
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				/>

				<LoadPopupPanel visible={resubmitChangeOrder.isLoading || sendSigningLink.isLoading} />

			</Popup>

		</>
	)
}
